import { FC, memo, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { SVG_MUI } from "@ryde/assets";
import {
  CourseIndicatorIcon,
  FramerTooltip,
  HubIndicatorRides,
  RideMlPathIndicator,
  RidePassengersHasDog,
  RideStatusFilledIndicator,
  VehicleOccupancyIconAndColoredLabel,
} from "@ryde/controls";
import { useAreaTitles } from "@ryde/redux";
import {
  DirectionEnum,
  durationInMinutesCeil,
  getRideRouteType,
  IRideTableDto,
  NO_VALUE_DISPLAY,
} from "@ryde/shared";

import { KanbanDateBox } from "../controls";
import {
  DurationWrapper,
  KanbanItemLine2,
  KanbanItemLine3,
  KanbanItemLineFrom,
  KanbanItemLineMapPlace,
  KanbanItemLineTo,
  OvalDirectionWrapper,
  VerticalLine1,
} from "../rides.kanban.item.styled";

/* may be used depending on user rights (to use more lightweight control)  */
const timeIsEditable = true;

interface IRideKanbanItemCenter {
  dto: IRideTableDto;
}

export const RideKanbanItemCenter: FC<IRideKanbanItemCenter> = memo(
  ({ dto }) => {
    const { t } = useTranslation();

    const areaIds = useMemo(() => {
      return dto?.metadata?.areaIds?.map(x => Number(x)) || [];
    }, [dto?.metadata?.areaIds]);

    const { areaTitlesCombined } = useAreaTitles(areaIds);

    const isMlPathIndicatorEnabled = useMemo(() => {
      const { customStationRouteId, excludedStationRouteIds = [] } =
        dto.metadata || {};
      if (!customStationRouteId || !excludedStationRouteIds?.length) {
        return Boolean(customStationRouteId);
      }
      const excludedStationRouteIdsSet = new Set(excludedStationRouteIds);
      return !excludedStationRouteIdsSet.has(customStationRouteId);
    }, [dto.metadata]);

    return (
      <>
        <KanbanItemLine2>
          <RidePassengersHasDog
            passengers={dto.metadata?.passengersInfo ?? []}
          />
          <VehicleOccupancyIconAndColoredLabel
            $busOccupancy={dto.occupancy}
            seatsCount={dto.metadata?.seatsCount}
            vehicleCapacity={dto.metadata?.maxPassengers}
            rideStatus={dto.status}
          />
          <HubIndicatorRides rideDto={dto} />
          <VerticalLine1 />
          {dto.metadata?.totalDuration && (
            <DurationWrapper $busOccupancy={dto.occupancy}>
              {dto?.metadata?.totalDuration
                ? durationInMinutesCeil(dto.metadata.totalDuration) +
                  " " +
                  t(`general.MIN`)
                : NO_VALUE_DISPLAY}
            </DurationWrapper>
          )}
          {dto.occupancy && <RideStatusFilledIndicator status={dto.status} />}
        </KanbanItemLine2>
        <KanbanItemLine3>
          {!!dto.courseId && !isMlPathIndicatorEnabled && (
            <FramerTooltip content={`${dto.courseId}: ${dto.courseName}`}>
              <span className="course">
                <CourseIndicatorIcon routeType={getRideRouteType(dto)} />
                <div className="kanban_course_name truncate-text">
                  {dto.courseName || dto.courseId}
                </div>
              </span>
            </FramerTooltip>
          )}

          {isMlPathIndicatorEnabled && <RideMlPathIndicator {...dto} />}

          <OvalDirectionWrapper>
            {dto.direction === DirectionEnum.PickUp && (
              <FramerTooltip content={t("selectListLabel.DirectionA")}>
                <SVG_MUI.OvalTo2SVG data-e2e="svg-pickup" />
              </FramerTooltip>
            )}
            {dto.direction !== DirectionEnum.PickUp && (
              <FramerTooltip content={t("selectListLabel.DirectionB")}>
                <SVG_MUI.OvalBack2SVG data-e2e="svg-dropoff" />
              </FramerTooltip>
            )}
          </OvalDirectionWrapper>
        </KanbanItemLine3>
        <KanbanItemLineFrom>
          <KanbanDateBox
            type="IN"
            rideId={dto.id}
            timeWithDate={dto.rideStart}
            timeIsEditable={timeIsEditable}
          />
          <label>{dto.originStationName}</label>
        </KanbanItemLineFrom>
        <KanbanItemLineTo>
          <KanbanDateBox
            type="OUT"
            rideId={dto.id}
            timeWithDate={dto.rideEnd}
            timeIsEditable={timeIsEditable}
          />

          <label>{dto.destinationStationName}</label>
        </KanbanItemLineTo>
        <KanbanItemLineMapPlace>
          <FramerTooltip content={t("ride.formLabel.innerAreaIds")}>
            <div className="kanban_areas_container">
              <SVG_MUI.LocationDot2SVG />
              <div className="kanban_areas truncate-text">
                {areaTitlesCombined || NO_VALUE_DISPLAY}
              </div>
            </div>
          </FramerTooltip>
        </KanbanItemLineMapPlace>
      </>
    );
  },
);
